import { batch } from 'editor/src/store/batchedSubscribeEnhancer';
import { applyReflowStateAction, updateHiddenElementsAction } from 'editor/src/store/design/slice';
import { Spread } from 'editor/src/store/design/types';
import { getSpreadQrSourceTextUuids } from 'editor/src/store/design/util/qrSourceTexts';
import {
  getOrphanSlotLayoutIds,
  getSlotFrameHiddenUuids,
} from 'editor/src/store/design/util/slotLayout/slotFrameVisibility';
import removeAllSelectedMediaElementsOperation from 'editor/src/store/editor/operation/removeAllSelectedMediaElementsOperation';
import getCurrentSpreadIndex from 'editor/src/store/editor/selector/getCurrentSpreadIndex';
import type { ThunkDispatch } from 'editor/src/store/hooks';
import { RootState } from 'editor/src/store/index';

import { captureMessage } from 'editor/src/logger';

import computeReflowState from './computeReflowState';
import getHiddenMediaElementUuids from './getHiddenMediaElementUuids';

const reportedLayoutIds = new Set<string>();

// A design that lost a layout but kept its tagged layers shows every frame at once; reported
// once per layout, from here rather than from the pure lookups the selectors share.
function reportOrphanSlotLayouts(spread: Pick<Spread, 'slotLayouts' | 'pages'>) {
  getOrphanSlotLayoutIds(spread).forEach((layoutId) => {
    if (!reportedLayoutIds.has(layoutId)) {
      reportedLayoutIds.add(layoutId);
      captureMessage('Slot frame layer without its layout', { extra: { layoutId } });
    }
  });
}

/**
 * Sole entry point for recomputing derived spread state. Four producers feed
 * `element.hidden`: Conditions, slot frames, Reflow rows and the texts an artwork-only
 * QR reads (`qrSourceTexts`), which Reflow treats like texts a Condition hid. Reflow
 * additionally mutates `element.y` (collapsed rows stay at authored position; subsequent
 * rows shift up to overlap them) and records the applied shift on each row.
 *
 * The hide channel and the position channel are written in a single batch so
 * render sees a consistent state. NEVER dispatch `updateHiddenElementsAction`
 * or `applyReflowStateAction` directly from anywhere else — that would
 * silently un-hide every other producer's elements OR leave positions out of
 * sync with collapse state.
 *
 * `releasedUuids` are texts a QR stopped reading. A spread with no producer left is
 * otherwise never recomputed, so they are un-hidden here, and nothing else on it.
 */
const updateHiddenElementsOperation =
  (targetSpreadIndex?: number, releasedUuids?: Set<number>) => (dispatch: ThunkDispatch, getState: () => RootState) => {
    const state = getState();
    const spreadIndex = targetSpreadIndex ?? getCurrentSpreadIndex(state);
    const spread = state.design.designData?.spreads[spreadIndex];
    if (!spread) {
      return;
    }

    reportOrphanSlotLayouts(spread);
    const hasConditions = !!spread.conditionGroup;
    const hasSlotLayouts = !!spread.slotLayouts?.length;
    const hasReflowGroups = !!spread.reflowGroups?.length;
    const qrSourceTexts = getSpreadQrSourceTextUuids(state.design.designData, spread);
    if (!hasConditions && !hasReflowGroups && !hasSlotLayouts && !qrSourceTexts.size) {
      const hidden = spread.pages.flatMap((page) => page.groups.media ?? []).filter((element) => element.hidden);
      if (hidden.some(({ uuid }) => releasedUuids?.has(uuid))) {
        const stillHidden = hidden.filter(({ uuid }) => !releasedUuids?.has(uuid)).map(({ uuid }) => uuid);
        dispatch(updateHiddenElementsAction({ spreadIndex, hiddenElementIds: stillHidden }));
      }
      return;
    }

    const conditionHidden = new Set<number>([
      ...(hasConditions ? getHiddenMediaElementUuids(spread.conditionGroup!, spread) : getSlotFrameHiddenUuids(spread)),
      ...qrSourceTexts,
    ]);
    const reflow = computeReflowState(spread, conditionHidden);
    const hiddenElementIds = new Set<number>([...conditionHidden, ...reflow.hiddenUuids]);

    batch(() => {
      dispatch(
        updateHiddenElementsAction({
          spreadIndex,
          hiddenElementIds: [...hiddenElementIds],
        }),
      );
      if (reflow.elementUpdates.length || reflow.rowShiftUpdates.length) {
        dispatch(
          applyReflowStateAction({
            spreadIndex,
            elementUpdates: reflow.elementUpdates,
            rowShiftUpdates: reflow.rowShiftUpdates,
          }),
        );
      }
    });

    if (state.editor.selectedElementUuids.some((uuid) => hiddenElementIds.has(uuid))) {
      dispatch(removeAllSelectedMediaElementsOperation());
    }
  };

export default updateHiddenElementsOperation;
